import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { notificationAPI } from '../services/api';
import { playNotificationBuzzer } from '../utils/notificationSound';
import { PASSENGER_NOTIFICATION_TYPES, PROVIDER_NOTIFICATION_TYPES } from '../constants/notificationTypes';
import { ensurePushSubscription } from '../utils/pushSubscription';

/* ─────────────────────────────────────────────────────────────────────────────
   PassengerNotificationContext — real-time notifications for the passenger
   portal. The list itself lives inside the profile page (no separate bell
   icon in the nav), but a brand-new notification still interrupts with a
   popup + sound regardless of what page the passenger is on, since that's
   the whole point of a notification.

   Delivery is push, not polling: the backend already has a full Socket.io
   pipeline (notificationService.js emits to a `user:<id>` room on every
   booking/ticket/quote/etc. event) that nothing in the passenger app was
   listening to — this just subscribes to it, mirroring the exact
   `io(url, { withCredentials: true })` + cookie-auth pattern already
   proven in TripTrackingPage.jsx.
───────────────────────────────────────────────────────────────────────────── */
const PassengerNotificationContext = createContext(null);

// Despite the name/comment history, this now resolves whoever's logged in
// on THIS device across portals — passenger session (localStorage,
// synchronous at login) or transport-provider session (localStorage marker
// written by TransportProviderAuthContext once its async me()/login()
// resolves — see that file's own comment on why the extra event listener
// below exists). Office portal isn't wired in yet.
//
// The two markers aren't mutually exclusive — the same browser can be
// logged into the passenger AND transport-provider portals at once (e.g.
// someone testing both sides of the marketplace on one device), so both
// localStorage entries can exist simultaneously. Always preferring
// passenger_user regardless of which portal is actually on screen meant a
// provider who'd ever also logged in as a passenger on that device would
// silently subscribe to their passenger identity's socket room while
// browsing the provider portal — the notification was correctly sent to
// their provider account server-side, it just never reached this socket.
// Checking the current path first fixes that; the unconditional fallback
// after covers pages outside both portals (e.g. public landing).
const getPassengerId = () => {
  const inProviderPortal = window.location.pathname.startsWith('/transport-provider');
  if (inProviderPortal) {
    const providerId = localStorage.getItem('transport_provider_user_id');
    if (providerId) return providerId;
  }
  try {
    const u = JSON.parse(localStorage.getItem('passenger_user') || '{}');
    if (u?.id) return u.id;
  } catch { /* ignore */ }
  return localStorage.getItem('transport_provider_user_id') || null;
};

export const PassengerNotificationProvider = ({ children }) => {
  const location = useLocation();
  const navigate  = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [userId, setUserId] = useState(getPassengerId());
  const socketRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getAll();
      setNotifications(res.data.data || []);
    } catch { /* silent — notifications are non-critical */ }
  }, []);

  // localStorage isn't reactive on its own — re-check on every navigation,
  // since login/logout always navigate somewhere. Only updates state (and
  // therefore only triggers the socket effect below) when the id actually
  // changes, not on every route change.
  useEffect(() => {
    const current = getPassengerId();
    setUserId((prev) => (prev !== current ? current : prev));
  }, [location.pathname]);

  // Transport-provider sessions resolve async (see TransportProviderAuthContext's
  // own comment) — a fresh page load there may never trigger the pathname
  // effect above before the marker is written, so this catches that case too.
  useEffect(() => {
    const recheck = () => {
      const current = getPassengerId();
      setUserId((prev) => (prev !== current ? current : prev));
    };
    window.addEventListener('saferNotifiableUserChanged', recheck);
    return () => window.removeEventListener('saferNotifiableUserChanged', recheck);
  }, []);

  // Connect/reconnect only when the logged-in passenger actually changes —
  // NOT on every navigation, so the socket persists across page changes
  // instead of tearing down and reconnecting on every route swap.
  useEffect(() => {
    if (!userId) { setNotifications([]); return; }

    fetchNotifications();
    ensurePushSubscription(); // background push — see that file's own comment

    const serverUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
    // Same portal disambiguation as getPassengerId() above and the
    // X-Safer-Portal header in api.js — tells app.js's io.use() which of
    // this browser's session cookies to authenticate the socket with, so a
    // provider who's also logged in as a passenger on this device doesn't
    // get their socket silently authenticated as the wrong account.
    const portal = window.location.pathname.startsWith('/transport-provider') ? 'transport-provider' : 'passenger';
    const socket = io(serverUrl, { transports: ['websocket', 'polling'], withCredentials: true, query: { portal } });
    socketRef.current = socket;

    socket.on('connect', () => socket.emit('subscribe:user', userId));

    socket.on('notification', (notif) => {
      // Read at event time (not effect-setup time) since this socket effect
      // doesn't re-run on navigation, so a captured `location` here could be
      // stale. /profile is a passenger-only route — a provider-portal
      // session has nowhere equivalent to send the click yet, so it just
      // dismisses there instead of navigating somewhere broken.
      const inPassengerPortal = !window.location.pathname.startsWith('/transport-provider')
        && !window.location.pathname.startsWith('/office')
        && !window.location.pathname.startsWith('/admin');

      // Same User account can be both a passenger and transport-provider
      // staff — a notification meant for the OTHER role shouldn't pop up
      // (or buzz) while browsing this one. The socket delivers everything
      // for this userId regardless of portal, so filter client-side.
      const relevantTypes = inPassengerPortal ? PASSENGER_NOTIFICATION_TYPES : PROVIDER_NOTIFICATION_TYPES;
      if (!relevantTypes.includes(notif.type)) return;

      setNotifications((prev) => [notif, ...prev]);
      playNotificationBuzzer();
      toast(({ closeToast }) => (
        <div dir="rtl" className={inPassengerPortal ? 'cursor-pointer' : ''} onClick={() => { if (inPassengerPortal) navigate('/profile'); closeToast(); }}>
          <p className="font-bold text-sm text-gray-900">{notif.title}</p>
          {notif.body && <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{notif.body}</p>}
        </div>
      ), { autoClose: 7000 });
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, [userId, fetchNotifications, navigate]);

  const markRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch { /* silent */ }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <PassengerNotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead, refresh: fetchNotifications }}>
      {children}
    </PassengerNotificationContext.Provider>
  );
};

export const usePassengerNotifications = () => useContext(PassengerNotificationContext);
